import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { FaAngleLeft, FaLock, FaEnvelope, FaEye, FaEyeSlash, FaCheckCircle, FaClock, FaPaperPlane, FaSms } from 'react-icons/fa';
import { handleTokenAndPostRequest } from '../utils/utils';
import { useNavigate } from 'react-router-dom';

function SignInModal(props) {
  const [email, setEmail] = useState(props.initialEmail || '');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [code, setCode] = useState(Array(6).fill(''));
  const [isCodeValid, setIsCodeValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [unregistered, setUnregistered] = useState(false);
  const [needsSignupConfirmation, setNeedsSignupConfirmation] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [showLoginChoice, setShowLoginChoice] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(props.initialStep === 'email');
  const [isSignInFlow, setIsSignInFlow] = useState(false);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const codeRefs = useRef([]);
  const navigate = useNavigate();

  const isInnerStep = isCodeSent || showLoginChoice || showPasswordInput || showEmailForm || needsSignupConfirmation;

  const resetModalState = () => {
    setEmail('');
    setIsCodeSent(false);
    setCode(Array(6).fill(''));
    setIsCodeValid(true);
    setErrorMessage('');
    setIsLoading(false);
    setUnregistered(false);
    setNeedsSignupConfirmation(false);
    setHasPassword(false);
    setShowLoginChoice(false);
    setShowPasswordInput(false);
    setShowEmailForm(false);
    setIsSignInFlow(false);
    setPassword('');
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSignIn = async () => {
    if (!validateEmail(email)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }
    setIsLoading(true);
    setErrorMessage('');
    setUnregistered(false);
    setNeedsSignupConfirmation(false);
    try {
      // First check if the agent has a password set
      const checkRes = await handleTokenAndPostRequest(
        `${window.api_host}/check-password-agent`,
        { email },
        { 'Content-Type': 'application/json' }
      );
      if (checkRes.data.has_password) {
        setHasPassword(true);
        setShowLoginChoice(true);
        setIsLoading(false);
        return;
      }
      // No password set — send code directly (or get back a signup-confirm prompt)
      const sendRes = await handleTokenAndPostRequest(
        `${window.api_host}/send-code-to-email-agent`,
        { email },
        { 'Content-Type': 'application/json' }
      );
      if (sendRes?.data?.needs_signup_confirmation) {
        setNeedsSignupConfirmation(true);
        return;
      }
      setIsCodeSent(true);
      setTimeout(() => codeRefs.current[0]?.focus(), 100);
    } catch (error) {
      setErrorMessage('Failed to send email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSignup = async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/send-code-to-email-agent`,
        { email, confirm_signup: true },
        { 'Content-Type': 'application/json' }
      );
      setNeedsSignupConfirmation(false);
      setIsCodeSent(true);
      setTimeout(() => codeRefs.current[0]?.focus(), 100);
    } catch (error) {
      setErrorMessage('Could not create your account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChooseCode = async () => {
    setIsLoading(true);
    setErrorMessage('');
    setShowLoginChoice(false);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/send-code-to-email-agent`,
        { email },
        { 'Content-Type': 'application/json' }
      );
      setIsCodeSent(true);
      setTimeout(() => codeRefs.current[0]?.focus(), 100);
    } catch (error) {
      setErrorMessage('Failed to send code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChoosePassword = () => {
    setShowLoginChoice(false);
    setShowPasswordInput(true);
    setTimeout(() => passwordRef.current?.focus(), 100);
  };

  const handlePasswordLogin = async () => {
    if (!password) {
      setErrorMessage('Please enter your password.');
      return;
    }
    setIsLoading(true);
    setErrorMessage('');
    try {
      const response = await handleTokenAndPostRequest(
        `${window.api_host}/login-password-agent`,
        { email, password },
        { 'Content-Type': 'application/json' }
      );
      if (response.data.valid) {
        localStorage.setItem('jwt_token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        resetModalState();
        props.onHide();
        if (props.onSuccess) props.onSuccess(email);
        const redirectPath = sessionStorage.getItem('redirect_after_login');
        sessionStorage.removeItem('redirect_after_login');
        navigate(redirectPath || '/getting-started');
      } else {
        setErrorMessage(response.data.message || 'Invalid email or password.');
      }
    } catch (error) {
      setErrorMessage('Failed to login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    try {
      await handleTokenAndPostRequest(
        `${window.api_host}/send-code-to-email-agent`,
        { email },
        { 'Content-Type': 'application/json' }
      );
      setIsCodeValid(true);
      setErrorMessage('');
      setCode(Array(6).fill(''));
      setTimeout(() => codeRefs.current[0]?.focus(), 100);
    } catch (error) {
      setErrorMessage('Failed to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeInput = (value, index) => {
    if (value.length > 1) {
      const newCode = [...code];
      value.split('').forEach((char, idx) => {
        if (index + idx < 6) {
          newCode[index + idx] = char;
        }
      });
      setCode(newCode);
      const nextIndex = index + value.length;
      if (nextIndex < 6) {
        codeRefs.current[nextIndex]?.focus();
      }
    } else {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      if (value !== '' && index < 5) {
        codeRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showPasswordInput) {
        handlePasswordLogin();
      } else if (isCodeSent) {
        validateCode();
      } else {
        handleSignIn();
      }
    } else if (e.key === 'Backspace' && code[index] === '' && index > 0) {
      const newCode = [...code];
      newCode[index - 1] = '';
      setCode(newCode);
      codeRefs.current[index - 1]?.focus();
    }
  };

  useEffect(() => {
    if (code.every(digit => digit !== '')) {
      validateCode();
    }
    // eslint-disable-next-line
  }, [code]);

  const validateCode = async () => {
    setIsLoading(true);
    try {
      const response = await handleTokenAndPostRequest(
        `${window.api_host}/validate-code-agent`,
        { email, code: code.join('') },
        { 'Content-Type': 'application/json' }
      );
      if (response.data.valid) {
        localStorage.setItem('jwt_token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));         
        resetModalState();
        props.onHide();
        if (props.onSuccess) props.onSuccess(email);
        setIsCodeValid(true);
        const redirectPath = sessionStorage.getItem('redirect_after_login');
        sessionStorage.removeItem('redirect_after_login');
        navigate(redirectPath || '/getting-started');
      } else {
        setIsCodeValid(false);
        setErrorMessage('Invalid code. Please try again.');
      }
    } catch (error) {
      setErrorMessage('Failed to validate code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (props.show && showEmailForm && emailRef.current) {
      emailRef.current.focus();
    }
  }, [props.show, showEmailForm]);

  useEffect(() => {
    if (!props.show) {
      resetModalState();
    }
  }, [props.show]);

  const trialPerks = [
    { icon: <FaClock style={{ color: '#10b981' }} />, text: '10 mins to get set up' },
    { icon: <FaCheckCircle style={{ color: '#10b981' }} />, text: 'Your logo & colours ready' },
  ];

  return (
    <Modal
      style={{ zIndex: 9999 }}
      show={props.show}
      onHide={props.onHide}
      onExited={resetModalState}
      centered
      fullscreen="sm-down"
    >
      <Modal.Header closeButton style={{ border: 'none', paddingBottom: 0 }}>
        <Modal.Title>
          {isInnerStep ? (
            <>
              <Button
                variant="link"
                onClick={() => {
                  if (needsSignupConfirmation) {
                    setNeedsSignupConfirmation(false);
                    setErrorMessage('');
                  } else if (isCodeSent || showLoginChoice || showPasswordInput) {
                    setIsCodeSent(false);
                    setShowLoginChoice(false);
                    setShowPasswordInput(false);
                    setErrorMessage('');
                    setCode(Array(6).fill(''));
                    setPassword('');
                  } else {
                    setShowEmailForm(false);
                    setErrorMessage('');
                  }
                }}
                disabled={isLoading}
                style={{
                  margin: 0,
                  marginBottom: 6,
                  padding: 0,
                  fontSize: '1.4rem',
                  color: "#000",
                  minWidth: 32,
                  minHeight: 32,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <FaAngleLeft />
              </Button>
              <span>{showLoginChoice ? 'Choose Login Method' : showPasswordInput ? 'Enter Password' : isCodeSent ? 'Enter Code' : needsSignupConfirmation ? 'Create Account' : isSignInFlow ? 'Sign In' : 'Log In & Explore'}</span>
            </>
          ) : (
            <span style={{ fontSize: '1.15rem', fontWeight: 700 }}>Your account is ready</span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ paddingTop: isInnerStep ? undefined : 8 }}>
        {!isInnerStep ? (
          <>
            <div style={{
              background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfeff 100%)',
              borderRadius: 12,
              padding: '20px 22px',
              marginBottom: 20,
              border: '1px solid #d1fae5',
            }}>
              <p style={{ margin: '0 0 4px', fontSize: '0.95rem', color: '#374151', lineHeight: 1.6 }}>
                We've already set up an account with <b>your logo and brand colours</b>. Just sign in with your work email to explore it &mdash; it's free forever.
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 22 }}>
              {trialPerks.map((perk, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.88rem', color: '#374151' }}>
                  {perk.icon}
                  <span>{perk.text}</span>
                </div>
              ))}
            </div>

            <Button
              onClick={() => {
                setIsSignInFlow(false);
                setShowEmailForm(true);
                setTimeout(() => emailRef.current?.focus(), 100);
              }}
              style={{
                width: '100%',
                border: 'none',
                borderRadius: '8px',
                padding: '0.85rem 1.5rem',
                fontSize: '1.05rem',
                fontWeight: 700,
                background: 'linear-gradient(90deg, #1a3f6b, #10b981)',
                color: '#fff',
                boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(16,185,129,0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(16,185,129,0.3)';
              }}
            >
              Log In & Explore
            </Button>

            <p style={{ textAlign: 'center', marginTop: 8, marginBottom: 0, fontSize: '0.8rem', color: '#9ca3af' }}>
              No credit card needed. Your account is free forever.
            </p>
          </>
        ) : needsSignupConfirmation ? (
          <>
            <p style={{ margin: '0 0 12px', fontSize: '0.95rem', color: '#374151' }}>
              No account exists for <b>{email}</b>.
            </p>
            <p style={{ margin: '0 0 16px', fontSize: '0.95rem', color: '#374151' }}>
              Would you like to create a new Vendorleads account with this email?
              We'll send you a 6-digit code to verify it.
            </p>
            {errorMessage && <Alert variant="danger" style={{ marginTop: 16 }}>{errorMessage}</Alert>}
          </>
        ) : unregistered ? (
          <Alert variant="warning">
            Sorry, this email isn't registered at the moment. We'll be in touch to get you started or resolve the issue.
          </Alert>
        ) : showLoginChoice ? (
          <>
            <p>How would you like to sign in as <b>{email}</b>?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Button
                onClick={handleChoosePassword}
                disabled={isLoading}
                style={{
                  width: '100%',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.9rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(90deg, #1a3f6b, #10b981)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <FaLock /> Sign in with Password
              </Button>
              <Button
                onClick={handleChooseCode}
                disabled={isLoading}
                variant="outline-secondary"
                style={{
                  width: '100%',
                  borderRadius: '6px',
                  padding: '0.9rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                {isLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <><FaEnvelope /> Send me a code instead</>
                )}
              </Button>
            </div>
            {errorMessage && <Alert variant="danger" style={{ marginTop: 16 }}>{errorMessage}</Alert>}
          </>
        ) : showPasswordInput ? (
          <>
            <p>Enter your password to sign in as <b>{email}</b>.</p>
            <Form>
              <Form.Group controlId="formPassword">
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Password
                </Form.Label>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  size="lg"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  ref={passwordRef}
                  disabled={isLoading}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handlePasswordLogin();
                    }
                  }}
                  style={{ fontSize: 16, padding: '12px 14px', borderRadius: 10 }}
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    marginTop: 6,
                    cursor: 'pointer',
                    color: '#6b7280',
                    fontSize: 12,
                    userSelect: 'none',
                  }}
                >
                  {showPassword ? <FaEyeSlash size={12} /> : <FaEye size={12} />}
                  {showPassword ? 'Hide password' : 'Show password'}
                </span>
              </Form.Group>
              {errorMessage && <Alert variant="danger" style={{ marginTop: 16 }}>{errorMessage}</Alert>}
            </Form>
          </>
        ) : isCodeSent ? (
          <>
            <p>Email was sent to <b>{email}</b>. Check your spam or promotions folder if you can't find it.</p>
            <p>Enter the 6-digit code sent to your email.</p>
            <Row className="justify-content-center">
              {code.map((digit, index) => (
                <Col key={index} xs="auto" style={{ padding: '4px' }}>
                  <Form.Control
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleCodeInput(e.target.value, index)}
                    ref={(el) => (codeRefs.current[index] = el)}
                    disabled={isLoading}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={(e) => {
                      const paste = e.clipboardData.getData('text');
                      handleCodeInput(paste, index);
                      e.preventDefault();
                    }}
                    style={{ height: '70px', width: '50px', textAlign: 'center', fontSize: '2rem' }}
                  />
                </Col>
              ))}
            </Row>
            {!isCodeValid && errorMessage && <Alert variant="danger" style={{ marginTop: 16 }}>{errorMessage}</Alert>}
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: '0.95rem' }}>
              {isSignInFlow
                ? 'Enter your work email to sign in.'
                : 'Enter your work email to log in — we\u2019ve already set up your account for you.'}
            </p>
            <Form>
              <Form.Group controlId="formEmail">
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                  Email address
                </Form.Label>
                <Form.Control
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  size="lg"
                  placeholder="you@agency.com.au"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  ref={emailRef}
                  disabled={isLoading}
                  onKeyDown={handleKeyDown}
                  style={{ fontSize: 16, padding: '12px 14px', borderRadius: 10 }}
                />
              </Form.Group>
              {errorMessage && <Alert variant="danger" style={{ marginTop: 16 }}>{errorMessage}</Alert>}
            </Form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ border: 'none', paddingTop: 0 }}>
        {isInnerStep && needsSignupConfirmation && (
          <Button
            onClick={handleConfirmSignup}
            disabled={isLoading}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: '6px',
              padding: '0.7rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              background: 'linear-gradient(90deg, #1a3f6b, #10b981)',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" style={{ marginRight: 10 }} />
                <span>Creating account</span>
              </>
            ) : (
              'Yes, create my account & send code'
            )}
          </Button>
        )}
        {isInnerStep && !unregistered && !needsSignupConfirmation && !showLoginChoice && (showPasswordInput ? (
          <>
            <Button
              variant="link"
              onClick={() => {
                setShowPasswordInput(false);
                setShowLoginChoice(true);
                setPassword('');
                setErrorMessage('');
              }}
              disabled={isLoading}
              style={{ width: '100%' }}
            >
              Use a different login method
            </Button>
            <Button
              onClick={handlePasswordLogin}
              disabled={isLoading}
              style={{
                width: '100%',
                border: 'none',
                borderRadius: '6px',
                padding: '0.7rem 1.5rem',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                background: 'linear-gradient(90deg, #1a3f6b, #10b981)',
                color: '#fff'
              }}
            >
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" style={{ marginRight: 10 }} />
                  <span>Signing In</span>
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </>
        ) : isCodeSent ? (
          <>
            <Button variant="link" onClick={handleResendCode} disabled={isLoading} style={{ width: '100%' }}>
              Resend Code
            </Button>
            <Button
              onClick={validateCode}
              disabled={isLoading}
              style={{
                width: '100%',
                border: 'none',
                borderRadius: '6px',
                padding: '0.7rem 1.5rem',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                background: 'linear-gradient(90deg, #1a3f6b, #10b981)',
                color: '#fff'
              }}
            >
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" style={{ marginRight: 10 }} />
                  <span>Verifying</span>
                </>
              ) : (
                'Verify Code'
              )}
            </Button>
          </>
        ) : showEmailForm ? (
          <Button
            onClick={handleSignIn}
            disabled={isLoading}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: '6px',
              padding: '0.7rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              textDecoration: 'none',
              background: 'linear-gradient(90deg, #1a3f6b, #10b981)',
              color: '#fff'
            }}
          >
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" style={{ marginRight: 10 }} />
                <span>{isSignInFlow ? 'Signing in' : 'Logging in'}</span>
              </>
            ) : (
              isSignInFlow ? 'Sign In' : 'Log In & Explore'
            )}
          </Button>
        ) : null)}
      </Modal.Footer>
    </Modal>
  );
}

export default SignInModal;